import React, { memo, useCallback, useMemo } from 'react';
import { ProjectListBlockForm } from './ProjectListBlock';
import { FormCheckbox } from '@core/components/form';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { CategoryResponse } from '@business/entities/category';
import { CommonService } from '@business/services';
import { R } from '@core/utils/r';
import { Button } from '@core/components/Button';
import { SortableList } from '@modules/common/Sortable';
import { SortEnd } from 'react-sortable-hoc';
import { ProjectListCategoryItem } from './components/ProjectListCategoryItem';

export interface ProjectListBlockSettingsProps {
  form: ProjectListBlockForm;
  categories: CategoryResponse[];
}

export const ProjectListBlockSettings = memo((props: ProjectListBlockSettingsProps) => {
  const { form, categories } = props;
  const formValue = useNonNilObservable(form.value$);

  const categoryItems = useMemo(
    () =>
      CommonService.selectItems(
        R.filter(categories ?? [], c => !c.parentId || c.parentId === '0'),
        c => c.title,
        'id',
      ),
    [categories],
  );

  const categoryControls = useNonNilObservable(form.controls.categories.items$);

  const onSortEnd = useCallback(
    (sort: SortEnd) => {
      form.patchValue({ categories: R.move(formValue.categories, sort.oldIndex, sort.newIndex) });
    },
    [form, formValue.categories],
  );

  return (
    <div className="project-list-settings">
      <div>
        <FormCheckbox control={form.controls.showOptionItems} />
        <FormCheckbox control={form.controls.showListItems} />
        <div className="text-muted font-size-12">
          Each shows in its own section after the categories, with one VCC table per category. On a proposal: the options and list items attached to
          it. On a work order's Project List: all of its options and list items, accepted or not. Edit the headings on the block itself.
        </div>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
        <h3 className="m-0">Categories</h3>
        <Button color="primary" outline size="small" onClick={() => form.controls.categories.push()}>
          Add Category
        </Button>
      </div>

      <SortableList useDragHandle lockAxis="y" onSortEnd={onSortEnd}>
        {categoryControls.map((categoryCtrl, index) => (
          <ProjectListCategoryItem
            key={index}
            index={index}
            categoryIndex={index}
            categoryCtrl={categoryCtrl}
            categoryItems={categoryItems}
            onDelete={() => form.controls.categories.removeAt(index)}
          />
        ))}
      </SortableList>
    </div>
  );
});
